import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel,
  OutlinedInput,
  Chip,
  Button,
} from "@mui/material";
import {
  getPermissions,
  getPermissionsByRoleId,
  updateRolePermissions,
} from "../../services/permissionServices";
import { toast } from "react-toastify";

const modeloTraduccion = {
  Agreement: "Convenio",
  Indicator: "Indicador",
  Permission: "Permiso",
  Report: "Informe",
  Role: "Rol",
  User: "Usuario",
};

const accionTraduccion = {
  create: "Crear",
  read: "Ver",
  update: "Editar",
  destroy: "Eliminar",
  manage_permissions: "Gestionar permisos de un rol",
  manage: "Asignar rol",
  activate: "Activar",
  deactivate: "Desactivar"
};

export default function PermissionList({ selectedRoleId }) {
  const [permissions, setPermissions] = useState({});
  const [rolePermissions, setRolePermissions] = useState({});
  const [selectedModels, setSelectedModels] = useState([]);
  

  const permissionsList = useCallback(async () => {
    try {
      const response = await getPermissions();
      setPermissions(response.data);
      
    } catch (err) {
      if (!err?.response) {
        toast.error("No responde el servidor");
      }
    }
  }, []);

  const loadRolePermissions = useCallback(async () => {
    if (!selectedRoleId) return;
    try {
      const response = await getPermissionsByRoleId(selectedRoleId);
      
      // El backend devuelve el array directamente (no dentro de .data)
      // Validar si response es un array o tiene .data
      const permissionsData = Array.isArray(response) ? response : 
                             (response && Array.isArray(response.data)) ? response.data : [];
      
      setRolePermissions(() => {
        // Construir el objeto con la estructura esperada:
        // { model: { action: true, ... }, ... }
        const perms = {};
        permissionsData.forEach(({ model, action }) => {
          if (!perms[model]) perms[model] = {};
          perms[model][action] = true;
        });
        return perms;
      });
      // Aquí extraemos los modelos únicos para marcar en el selector
      const models = [...new Set(permissionsData.map((p) => p.model))];
      setSelectedModels(models);
    } catch (err) {
      toast.error("Error al obtener permisos del rol");
    }
  }, [selectedRoleId]);

  useEffect(() => {
    const fetchData = async () => {
      await permissionsList(); // carga todas las acciones posibles
      if (selectedRoleId) {
        await loadRolePermissions(); // marca solo las del rol actual
      } else {
        setRolePermissions({});
        setSelectedModels([]);
      }
    };
    fetchData();
  }, [permissionsList, loadRolePermissions, selectedRoleId]);

  const handleModelChange = (event) => {
    const value = event.target.value;

    // Si seleccionan "Todos los modelos"
    if (value.includes("all")) {
      const allModels = Object.keys(permissions);
      setSelectedModels(allModels);
      return;
    }

    // Asegurarse de que "all" no se quede en el array
    const filtered = value.filter((v) => v !== "all");
    setSelectedModels(filtered);

    // Remover permisos de modelos deseleccionados
    setRolePermissions((prev) => {
      const updated = { ...prev };
      Object.keys(prev).forEach((model) => {
        if (!filtered.includes(model)) {
          delete updated[model];
        }
      });
      return updated;
    });
  };

  const handlePermissionToggle = (model, action) => {
    setRolePermissions((prev) => ({
      ...prev,
      [model]: {
        ...(prev[model] || {}),
        [action]: !prev?.[model]?.[action],
      },
    }));
  };

  const toggleAllActionsForModel = (model) => {
    const current = rolePermissions[model] || {};
    const allSelected = Object.keys(permissions[model] || {}).every(
      (action) => current[action]
    );

    setRolePermissions((prev) => ({
      ...prev,
      [model]: Object.fromEntries(
        Object.keys(permissions[model] || {}).map((action) => [
          action,
          !allSelected,
        ])
      ),
    }));
  };

  const toggleAllActionsForAllModels = () => {
    const allSelected = selectedModels.every((model) =>
      Object.keys(permissions[model] || {}).every(
        (action) => rolePermissions[model]?.[action]
      )
    );

    const newPermissions = { ...rolePermissions };

    selectedModels.forEach((model) => {
      newPermissions[model] = Object.fromEntries(
        Object.keys(permissions[model] || {}).map((action) => [
          action,
          !allSelected,
        ])
      );
    });

    setRolePermissions(newPermissions);
  };

  const isAllChecked = selectedModels.every((model) =>
    Object.keys(permissions[model] || {}).every(
      (action) => rolePermissions[model]?.[action]
    )
  );

  const isSomeChecked =
    selectedModels.some((model) =>
      Object.keys(permissions[model] || {}).some(
        (action) => rolePermissions[model]?.[action]
      )
    ) && !isAllChecked;

  // Convierte rolePermissions a un array plano [{ model, action }]
  const preparePayload = () => {
    const payload = [];
    Object.entries(rolePermissions).forEach(([model, actions]) => {
      Object.entries(actions).forEach(([action, allowed]) => {
        if (allowed === true) {
          payload.push({ model, action });
        }
      });
    });
    return payload;
  };

  // Función para guardar los permisos
  const saveRolePermissions = async () => {
    if (!selectedRoleId) {
      toast.error("No hay rol seleccionado");
      return;
    }

    const payload = preparePayload();

    try {
      await updateRolePermissions(selectedRoleId, payload);
      await loadRolePermissions();
      toast.success("Permisos actualizados");
    } catch (error) {
      toast.error("Error al actualizar permisos");
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        Lista de permisos
      </Typography>

      {/* Selector múltiple de modelos */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="multi-model-select-label">
          Seleccionar modelos
        </InputLabel>
        <Select
          labelId="multi-model-select-label"
          multiple
          value={selectedModels}
          onChange={handleModelChange}
          input={<OutlinedInput label="Seleccionar modelos" />}
          renderValue={(selected) => {
            if (!selected || selected.length === 0) {
              return (
                <em style={{ color: "#888" }}>Ningún modelo seleccionado</em>
              );
            }

            const models = selected.filter((val) => val !== "all");

            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {models.map((value) => (
                  <Chip key={value} label={modeloTraduccion[value] || value} />
                ))}
              </Box>
            );
          }}
        >
          <MenuItem value="all">
            <Checkbox
              checked={
                selectedModels.length === Object.keys(permissions).length
              }
            />
            Todos los modelos
          </MenuItem>

          {Object.keys(permissions).map((modelKey) => (
            <MenuItem key={modelKey} value={modelKey}>
              <Checkbox checked={selectedModels.includes(modelKey)} />
              {modeloTraduccion[modelKey] || modelKey}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Checkbox global para todos los modelos seleccionados */}
      {selectedModels.length > 0 && (
        <FormControlLabel
          control={
            <Checkbox
              checked={isAllChecked}
              indeterminate={isSomeChecked}
              onChange={toggleAllActionsForAllModels}
            />
          }
          label="Seleccionar todas las acciones de todos los modelos"
          sx={{ mb: 2 }}
        />
      )}

      {/* Lista de acciones por modelo seleccionado */}
      {selectedModels.map((model) => (
        <Box
          key={model}
          sx={{ mb: 3, border: "1px solid #ccc", p: 2, borderRadius: 1 }}
        >
          <Typography variant="h6" gutterBottom>
            {modeloTraduccion[model] || model}
          </Typography>

          {/* Checkbox para seleccionar todas las acciones del modelo */}
          <FormControlLabel
            control={
              <Checkbox
                checked={Object.keys(permissions[model] || {}).every(
                  (action) => rolePermissions[model]?.[action]
                )}
                indeterminate={
                  Object.keys(permissions[model] || {}).some(
                    (action) => rolePermissions[model]?.[action]
                  ) &&
                  !Object.keys(permissions[model] || {}).every(
                    (action) => rolePermissions[model]?.[action]
                  )
                }
                onChange={() => toggleAllActionsForModel(model)}
              />
            }
            label="Seleccionar todas las acciones"
            sx={{ mb: 1 }}
          />

          <Grid container spacing={2}>
            {Object.entries(permissions[model] || {}).map(
              ([action, allowed]) => (
                <Grid item key={`${model}-${action}`}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rolePermissions[model]?.[action] || false}
                        onChange={() => handlePermissionToggle(model, action)}
                      />
                    }
                    label={accionTraduccion[action] || action}
                  />
                </Grid>
              )
            )}
          </Grid>
        </Box>
      ))}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={saveRolePermissions}
        >
          Guardar permisos
        </Button>
      </Box>
    </Box>
  );
}
